import { useEffect, useRef, useState } from 'react';
import { executeCommand } from './commands';
import useDevMode from '../devmode/usedevmode';
import { isMobileTerminal } from '../../utils/isMobileTerminal';

export default function useTerminal() {
    const { exitDevMode } = useDevMode();
    const isMobile = isMobileTerminal();
    const canExitDev = !isMobile;

    const [lines, setLines] = useState([
        { type: 'out', text: 'Type "help" to get started.' },
        { type: 'spacer' },
    ]);

    const [input, setInput] = useState('');
    const [history, setHistory] = useState([]);
    const historyIndex = useRef(null);

    useEffect(() => {
        const runCommand = () => {
            const result = executeCommand(input, { canExitDev });

            setHistory(h => [...h, input]);
            historyIndex.current = null;

            if (result?.type === 'CLEAR') {
                setLines([]);
            } else if (result?.type === 'EXIT_DEV') {
                setLines(l => [
                    ...l,
                    { type: 'cmd', text: `$> ${input}` },
                    { type: 'out', text: 'Exiting dev mode...' },
                ]);
                setTimeout(exitDevMode, 300);
            } else {
                setLines(l => [
                    ...l,
                    { type: 'cmd', text: `$> ${input}` },
                    ...(Array.isArray(result) ? result : []),
                    { type: 'spacer' },
                ]);
            }

            setInput('');
        };

        

        const onKeyDown = (e) => {
            if (isMobile) return;

            if (e.key === 'Backspace') {
                e.preventDefault();
                setInput(v => v.slice(0, -1));
                return;
            }

            if (e.key === 'Enter') {
                e.preventDefault();
                runCommand();
                return;
            }

            if (e.key === 'ArrowUp') {
                e.preventDefault();
                if (!history.length) return;

                historyIndex.current =
                    historyIndex.current === null
                        ? history.length - 1
                        : Math.max(0, historyIndex.current - 1);

                setInput(history[historyIndex.current]);
                return;
            }

            if (e.key === 'ArrowDown') {
                e.preventDefault();
                if (historyIndex.current === null) return;

                if (historyIndex.current < history.length - 1) {
                    historyIndex.current++;
                    setInput(history[historyIndex.current]);
                } else {
                    historyIndex.current = null;
                    setInput('');
                }
                return;
            }

            if (
                e.key.length === 1 &&
                !e.ctrlKey &&
                !e.metaKey &&
                !e.altKey
            ) {
                setInput(v => v + e.key);
            }
        };

        window.addEventListener('keydown', onKeyDown);

        

        const hiddenInput = document.querySelector('.terminal-hidden-input');

        const onMobileInput = (e) => {
            setInput(e.target.value);
        };

        const onMobileKeyDown = (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                runCommand();
            }
        };

        if (hiddenInput) {
            hiddenInput.value = input;
            hiddenInput.addEventListener('input', onMobileInput);
            hiddenInput.addEventListener('keydown', onMobileKeyDown);
        }

        return () => {
            window.removeEventListener('keydown', onKeyDown);

            if (hiddenInput) {
                hiddenInput.removeEventListener('input', onMobileInput);
                hiddenInput.removeEventListener('keydown', onMobileKeyDown);
            }
        };
    }, [input, history, exitDevMode, canExitDev, isMobile]);

    return { lines, input };
}
